import React, { memo } from "react";
import cx from "classnames";
import { Tooltip } from "antd";
import { IconProps } from "antd/es/icon";
import { ChartSpinner } from "../ChartSpinner/ChartSpinner";
import styles from "./DashboardItemLayout.module.scss";
import DashboardItemFilter, {
  DashboardItemFilterProps,
  DashboardFilterValue as DashboardFilterValueLocal,
} from "./components/DashboardItemFilter/DashboardItemFilter";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import Icon from "app/components/Icon/Icon";
import { Switch } from "app/components/Common/Switch";
import { SwitchProps } from "antd/lib/switch";

interface Props {
  background?: boolean;
  children: React.ReactNode;
  title?: string;
  subTitle?: string;
  loading?: boolean;
  icons?: IconProps[];
  info?: string;
  switches?: SwitchProps[];
  filters?: DashboardItemFilterProps;
  suffix?: React.ReactNode;
}

const DashboardItemLayout = ({
  background,
  children,
  title,
  subTitle,
  loading,
  icons,
  info,
  switches,
  filters,
  suffix,
}: Props) => {
  return (
    <ErrorFallbackUI>
      <section
        className={cx(styles.container, { [styles.background]: background })}
      >
        <div className={styles.header}>
          {!!title && <h2 className={styles.title}>{title.toUpperCase()}</h2>}
          {!!subTitle && <h3 className={styles.subTitle}>{subTitle}</h3>}
          {(!!icons || !!info || !!suffix) && (
            <div className={styles.icons}>
              {suffix}
              {!!icons &&
                icons?.map((icon, index) => (
                  <Icon
                    key={`dashboard-item-icon${index}`}
                    {...icon}
                    className={cx(styles.icon, icon.className)}
                  />
                ))}
              {!!info && (
                <Tooltip title={info} arrowPointAtCenter placement="topRight">
                  <Icon type="info-circle" className={styles.icon} />
                </Tooltip>
              )}
            </div>
          )}
        </div>
        {!!filters && <DashboardItemFilter {...filters} />}
        {children}
        {!!switches && switches.length > 0 && (
          <div className={styles.footer}>
            {switches.map((s, index) => (
              <Switch
                {...s}
                className={cx(styles.checkbox, s.className)}
                key={index}
              />
            ))}
          </div>
        )}
        <ChartSpinner spinning={loading} />
      </section>
    </ErrorFallbackUI>
  );
};

export default memo(DashboardItemLayout);

export type DashboardFilterValue = DashboardFilterValueLocal;
